import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  deleteField,
  where,
  serverTimestamp,
  limit as fsLimit,
  startAfter,
  increment,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '../../firebase'
import { getAuthUserData } from '../../utils/authManager'

const CHANNELS_COLLECTION = 'chatChannels'

const buildCurrentUser = () => {
  const user = getAuthUserData() || {}
  const id = user._id || user.userRefId || user.id
  return {
    id: id ? String(id) : null,
    name: user.name || user.fullName || user.firstName || user.mobileNo || 'User',
    avatar: user.profileImage || user.avatar || ''
  }
}

// ─── Channels ────────────────────────────────────────────────────────────────

export const subscribeToUserChannels = callback => {
  const currentUser = buildCurrentUser()

  // If no user id yet, immediately return empty list so UI stops loading
  if (!currentUser.id) {
    console.warn('[Chat] subscribeToUserChannels: no currentUser.id — check getAuthUserData()', buildCurrentUser())
    callback([])
    return () => {}
  }

  // NOTE: Firestore operator is 'array-contains' (hyphen), NOT 'array_contains'
  const q = query(
    collection(db, CHANNELS_COLLECTION),
    where('memberIds', 'array-contains', currentUser.id)
  )

  return onSnapshot(q, snapshot => {
    const channels = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const aTime = a.lastMessageAt?.seconds || 0
        const bTime = b.lastMessageAt?.seconds || 0
        return bTime - aTime
      })
    callback(channels)
  })
}

export const createChannel = async ({ name, description, isPrivate, memberIds }) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')

  const baseMembers = Array.isArray(memberIds) && memberIds.length ? memberIds.map(String) : []
  const uniqueMembers = Array.from(new Set([currentUser.id, ...baseMembers]))

  const now = serverTimestamp()
  const channelDoc = await addDoc(collection(db, CHANNELS_COLLECTION), {
    name,
    description: description || '',
    isPrivate: !!isPrivate,
    channelType: isPrivate ? 'direct' : 'group',
    createdBy: currentUser.id,
    createdAt: now,
    lastMessageAt: now,
    lastMessageText: '',
    memberCount: uniqueMembers.length,
    memberIds: uniqueMembers,
    admins: [currentUser.id]
  })

  return channelDoc.id
}

export const joinChannel = async channelId => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')

  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  const snap = await getDoc(channelRef)
  if (!snap.exists()) throw new Error('Channel not found')

  const data = snap.data()
  const memberIds = Array.isArray(data.memberIds) ? data.memberIds : []

  if (!memberIds.includes(currentUser.id)) {
    await updateDoc(channelRef, {
      memberIds: arrayUnion(currentUser.id),
      memberCount: increment(1)
    })
  }
}

// ─── Messages ────────────────────────────────────────────────────────────────

export const subscribeToChannelMessages = (channelId, { pageSize = 40, onInitial, onMore } = {}) => {
  const messagesCol = collection(db, CHANNELS_COLLECTION, channelId, 'messages')
  const baseQuery = query(messagesCol, orderBy('createdAt', 'desc'), fsLimit(pageSize))

  let lastVisible = null
  let initialized = false

  const unsubscribe = onSnapshot(baseQuery, snapshot => {
    const docs = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))

    // Only update lastVisible on first load to avoid breaking loadMore cursor
    if (!initialized) {
      lastVisible = snapshot.docs[snapshot.docs.length - 1] || null
      initialized = true
    }

    if (onInitial) onInitial(docs)
  })

  const loadMore = async () => {
    if (!lastVisible) return []

    const moreQuery = query(
      messagesCol,
      orderBy('createdAt', 'desc'),
      startAfter(lastVisible),
      fsLimit(pageSize)
    )

    const moreSnap = await getDocs(moreQuery)
    if (moreSnap.empty) return []

    const docs = moreSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))

    lastVisible = moreSnap.docs[moreSnap.docs.length - 1] || lastVisible
    if (onMore) onMore(docs)
    return docs
  }

  return { unsubscribe, loadMore, hasMore: () => !!lastVisible }
}

/**
 * Send a message. Supports text, image, document, audio.
 * replyTo: { messageId, text, senderName, type, mediaName }
 */
export const sendMessage = async (channelId, { text, type = 'text', media, replyTo }) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')
  if (!text && !media) return

  const mediaPayload = media
    ? {
        mediaUrl: media.url,
        mediaType: media.contentType,
        mediaName: media.name,
        mediaSize: media.size
      }
    : {}

  const replyPayload = replyTo
    ? {
        replyTo: {
          messageId: replyTo.messageId || '',
          text: replyTo.text || '',
          senderName: replyTo.senderName || '',
          type: replyTo.type || 'text',
          mediaName: replyTo.mediaName || ''
        }
      }
    : {}

  const now = serverTimestamp()

  await addDoc(collection(db, CHANNELS_COLLECTION, channelId, 'messages'), {
    channelId,
    senderId: currentUser.id,
    senderName: currentUser.name,
    senderAvatar: currentUser.avatar || '',
    type,
    text: text || '',
    createdAt: now,
    deleted: false,
    edited: false,
    reactions: {},
    ...mediaPayload,
    ...replyPayload
  })

  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  const snap = await getDoc(channelRef)
  const data = snap.data() || {}
  const memberIds = Array.isArray(data.memberIds) ? data.memberIds : []

  const updates = {
    lastMessageAt: now,
    lastMessageText: text || (media ? `📎 ${media.name}` : ''),
    lastMessageSenderId: currentUser.id
  }

  memberIds
    .filter(id => id !== currentUser.id)
    .forEach(id => {
      updates[`unreadCountByUser.${id}`] = increment(1)
    })

  await updateDoc(channelRef, updates)
}

/**
 * Soft-delete a message: marks deleted=true and clears text/media
 */
export const deleteMessage = async (channelId, messageId) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')

  const msgRef = doc(db, CHANNELS_COLLECTION, channelId, 'messages', messageId)
  const snap = await getDoc(msgRef)
  if (!snap.exists()) throw new Error('Message not found')

  const data = snap.data()
  if (String(data.senderId) !== String(currentUser.id)) {
    throw new Error('You can only delete your own messages')
  }

  await updateDoc(msgRef, {
    deleted: true,
    text: '',
    type: 'deleted',
    mediaUrl: deleteField(),
    mediaName: deleteField(),
    mediaType: deleteField()
  })
}

/**
 * Edit a text message
 */
export const editMessage = async (channelId, messageId, newText) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')

  if (!newText || !newText.trim()) throw new Error('Message cannot be empty')

  const msgRef = doc(db, CHANNELS_COLLECTION, channelId, 'messages', messageId)
  const snap = await getDoc(msgRef)
  if (!snap.exists()) throw new Error('Message not found')

  const data = snap.data()
  if (String(data.senderId) !== String(currentUser.id)) {
    throw new Error('You can only edit your own messages')
  }
  if (data.type !== 'text') throw new Error('Only text messages can be edited')

  await updateDoc(msgRef, {
    text: newText.trim(),
    edited: true,
    editedAt: serverTimestamp()
  })
}

/**
 * Toggle a reaction emoji on a message.
 * If user already reacted with that emoji, it removes it.
 */
export const toggleReaction = async (channelId, messageId, emoji) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')

  const msgRef = doc(db, CHANNELS_COLLECTION, channelId, 'messages', messageId)
  const snap = await getDoc(msgRef)
  if (!snap.exists()) return

  const data = snap.data()
  const reactions = data.reactions || {}
  const users = reactions[emoji] || []
  const userId = currentUser.id

  if (users.includes(userId)) {
    // Remove reaction
    const newUsers = users.filter(id => id !== userId)
    if (newUsers.length === 0) {
      // Clean up key entirely
      const updated = { ...reactions }
      delete updated[emoji]
      await updateDoc(msgRef, { reactions: updated })
    } else {
      await updateDoc(msgRef, { [`reactions.${emoji}`]: arrayRemove(userId) })
    }
  } else {
    await updateDoc(msgRef, { [`reactions.${emoji}`]: arrayUnion(userId) })
  }
}

// ─── Presence / Read / Typing ────────────────────────────────────────────────

export const markChannelRead = async channelId => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) return

  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  await updateDoc(channelRef, {
    [`lastReadAtByUser.${currentUser.id}`]: serverTimestamp(),
    [`unreadCountByUser.${currentUser.id}`]: 0
  })
}

export const setTyping = async (channelId, isTyping) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) return

  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  await updateDoc(channelRef, {
    [`typingByUser.${currentUser.id}`]: isTyping ? serverTimestamp() : null
  })
}

export const updateOnlineStatus = async isOnline => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) return

  // Update across all user's channels — lightweight approach: store on user profile instead.
  // For now this is a no-op placeholder. Implement with Firebase Realtime Database
  // presence for true online/offline detection.
}

// ─── Calls (Agora signaling via Firestore) ───────────────────────────────────

export const subscribeToActiveCall = (channelId, callback) => {
  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  return onSnapshot(channelRef, snap => {
    const data = snap.data() || {}
    callback(data.activeCall || null)
  })
}

export const subscribeToIncomingRingingCalls = callback => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) {
    callback([])
    return () => {}
  }

  // Query channels where this user is a member and there is an active ringing call.
  // Note: Firestore may require a composite index for these where clauses.
  const q = query(
    collection(db, CHANNELS_COLLECTION),
    where('memberIds', 'array-contains', currentUser.id),
    where('activeCall.status', '==', 'ringing')
  )

  return onSnapshot(q, snapshot => {
    const calls = snapshot.docs
      .map(d => {
        const data = d.data() || {}
        return {
          channelId: d.id,
          channelName: data.name || '',
          activeCall: data.activeCall || null
        }
      })
      .filter(x => x.activeCall && String(x.activeCall.createdBy) !== String(currentUser.id))

    callback(calls)
  })
}

export const startCall = async (channelId, { type = 'voice' } = {}) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')
  if (!channelId) throw new Error('Channel not selected')

  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  const snap = await getDoc(channelRef)
  if (!snap.exists()) throw new Error('Channel not found')

  const data = snap.data() || {}
  const memberIds = Array.isArray(data.memberIds) ? data.memberIds : []
  if (!memberIds.includes(currentUser.id)) throw new Error('Not a member of this chat')

  const now = serverTimestamp()
  const channelName = `chat_${channelId}_${Date.now()}`
  const callType = type === 'video' ? 'video' : 'voice'

  const callDoc = await addDoc(collection(db, CHANNELS_COLLECTION, channelId, 'calls'), {
    channelId,
    channelName,
    type: callType,
    status: 'ringing', // ringing | accepted | ended
    createdBy: currentUser.id,
    memberIds,
    createdAt: now
  })

  const activeCall = {
    id: callDoc.id,
    channelId,
    channelName,
    type: callType,
    status: 'ringing',
    createdBy: currentUser.id,
    memberIds,
    createdAt: now
  }

  await updateDoc(channelRef, { activeCall })

  return activeCall
}

export const acceptCall = async (channelId, callId) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')
  if (!channelId || !callId) throw new Error('Call not found')

  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  const callRef = doc(db, CHANNELS_COLLECTION, channelId, 'calls', callId)

  await updateDoc(callRef, {
    status: 'accepted',
    acceptedBy: currentUser.id,
    acceptedAt: serverTimestamp()
  })

  await updateDoc(channelRef, {
    'activeCall.status': 'accepted',
    'activeCall.acceptedBy': currentUser.id,
    'activeCall.acceptedAt': serverTimestamp()
  })
}

export const endCall = async (channelId, callId, { reason } = {}) => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id) throw new Error('User not found')
  if (!channelId || !callId) return

  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  const callRef = doc(db, CHANNELS_COLLECTION, channelId, 'calls', callId)

  let callBefore = null
  try {
    const snap = await getDoc(callRef)
    callBefore = snap.exists() ? snap.data() : null
  } catch (_) {}

  try {
    await updateDoc(callRef, {
      status: 'ended',
      endedBy: currentUser.id,
      endedAt: serverTimestamp(),
      ...(reason ? { reason: String(reason) } : {})
    })
  } catch (_) {}

  // If the call ended without being accepted, count it as missed for recipients.
  // This enables a "missed calls" badge even if user never opened chat.
  try {
    const wasMissed = callBefore && callBefore.status === 'ringing' && !callBefore.acceptedAt
    if (wasMissed) {
      const memberIds = Array.isArray(callBefore.memberIds) ? callBefore.memberIds.map(String) : []
      const missedBy = memberIds.filter(id => id && id !== String(callBefore.createdBy))

      if (missedBy.length) {
        const updates = {
          missedBy,
          missed: true
        }
        missedBy.forEach(uid => {
          updates[`missedCallCountByUser.${uid}`] = increment(1)
        })
        await updateDoc(callRef, updates).catch(() => {})
        await updateDoc(channelRef, updates).catch(() => {})
      }
    }
  } catch (_) {}

  // Clear activeCall only if it matches this callId
  try {
    const snap = await getDoc(channelRef)
    const data = snap.data() || {}
    if (data.activeCall?.id === callId) {
      await updateDoc(channelRef, { activeCall: null })
    }
  } catch (_) {}
}

export const markMissedCallsSeen = async channelId => {
  const currentUser = buildCurrentUser()
  if (!currentUser.id || !channelId) return
  const channelRef = doc(db, CHANNELS_COLLECTION, channelId)
  await updateDoc(channelRef, {
    [`missedCallCountByUser.${currentUser.id}`]: 0
  }).catch(() => {})
}

export const fetchCallHistory = async (channelId, { limit = 20 } = {}) => {
  if (!channelId) return []
  const callsCol = collection(db, CHANNELS_COLLECTION, channelId, 'calls')
  const q = query(callsCol, orderBy('createdAt', 'desc'), fsLimit(limit))
  const snap = await getDocs(q)
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

// ─── Media ───────────────────────────────────────────────────────────────────

export const uploadMedia = async (file, { channelId, messageType }) => {
  if (!file) throw new Error('No file provided')

  const currentUser = buildCurrentUser()
  const timestamp = Date.now()
  const path = `chatMedia/${channelId}/${currentUser.id || 'anon'}/${timestamp}_${file.name}`

  const storageRef = ref(storage, path)
  const snapshot = await uploadBytes(storageRef, file)
  const url = await getDownloadURL(snapshot.ref)

  return {
    url,
    path,
    name: file.name,
    size: file.size,
    contentType: file.type,
    messageType
  }
}